import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Textarea, Button, Alert, Avatar, Card, CardHeader, CardContent } from '@/components/ui';
import { VenueCommentsService, type VenueComment } from '@/services/VenueCommentsService';
import { useNotificationStore } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { formatDistanceToNow } from 'date-fns';
import { MessageSquare, Edit2, Trash2, Send, ArrowUpDown } from 'lucide-react';

interface Props {
  venueId: string;
  venueName: string;
}

export function VenueComments({ venueId, venueName }: Props) {
  const [commentText, setCommentText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { push: addNotification } = useNotificationStore();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const service = new VenueCommentsService();

  // Query key for comments list
  const queryKey = ['venue-comments', venueId, sortOrder];

  // Fetch comments
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => service.list(venueId, { sortOrder })
  });

  // Create comment mutation
  const createMutation = useMutation({
    mutationFn: (text: string) => service.create(venueId, text),
    onSuccess: (newComment) => {
      // Manually update the cache instead of invalidating
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          items: [newComment.comment, ...old.items],
          pagination: {
            ...old.pagination,
            totalItems: old.pagination.totalItems + 1
          }
        };
      });
      setCommentText('');
      addNotification({
        type: 'success',
        title: 'Comment Posted',
        message: 'Your comment has been added successfully'
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Post',
        message: error instanceof Error ? error.message : 'Failed to post comment'
      });
    }
  });

  // Update comment mutation
  const updateMutation = useMutation({
    mutationFn: ({ commentId, text }: { commentId: string; text: string }) =>
      service.update(commentId, text),
    onSuccess: (updatedComment, variables) => {
      // Manually update the cache instead of invalidating
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((comment: VenueComment) =>
            comment.id === variables.commentId ? updatedComment : comment
          )
        };
      });
      setEditingId(null);
      setEditText('');
      addNotification({
        type: 'success',
        title: 'Comment Updated',
        message: 'Your comment has been updated successfully'
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Update',
        message: error instanceof Error ? error.message : 'Failed to update comment'
      });
    }
  });

  // Delete comment mutation
  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => service.delete(commentId),
    onSuccess: (_, deletedCommentId) => {
      // Manually update the cache instead of invalidating
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.filter((comment: VenueComment) => comment.id !== deletedCommentId),
          pagination: {
            ...old.pagination,
            totalItems: Math.max(0, old.pagination.totalItems - 1)
          }
        };
      });
      setDeleteConfirmId(null);
      addNotification({
        type: 'success',
        title: 'Comment Deleted',
        message: 'Comment has been deleted successfully'
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Delete',
        message: error instanceof Error ? error.message : 'Failed to delete comment'
      });
    }
  });

  const handleSubmit = (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const trimmed = commentText.trim();
    if (trimmed.length > 0 && trimmed.length <= 2000 && !createMutation.isPending) {
      createMutation.mutate(trimmed);
    }
  };

  const handleEdit = (comment: VenueComment) => {
    setEditingId(comment.id);
    setEditText(comment.comment_text);
  };

  const handleSaveEdit = (commentId: string) => {
    const trimmed = editText.trim();
    if (trimmed.length > 0 && trimmed.length <= 2000) {
      updateMutation.mutate({ commentId, text: trimmed });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleDeleteConfirm = (commentId: string) => {
    deleteMutation.mutate(commentId);
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  const canEditComment = (comment: VenueComment) => {
    return user?.id === comment.user_id;
  };

  const canDeleteComment = (comment: VenueComment) => {
    const isOwner = user?.id === comment.user_id;
    const isSuperAdmin = user?.isSuperAdmin;
    return isOwner || isSuperAdmin;
  };

  const comments = data?.items || [];
  const commentCount = data?.pagination.totalItems || 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-gray-600">Loading comments...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        Failed to load comments. Please try again.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with count and sort toggle */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-primary-600" />
          Team Comments {commentCount > 0 && `(${commentCount})`}
        </h3>
        {comments.length > 1 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={toggleSortOrder}
            className="flex items-center gap-1"
          >
            <ArrowUpDown className="w-4 h-4" />
            {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
          </Button>
        )}
      </div>

      {/* Info alert about purpose */}
      <Alert variant="info">
        Share your experiences with this venue to help team members make informed decisions. 
        All organization members can view these comments.
      </Alert>

      {/* Add comment form */}
      <div className="space-y-3">
        <Textarea
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder={`Share your experience with ${venueName}...`}
          rows={4}
          maxLength={2000}
          className="resize-y"
          disabled={createMutation.isPending}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
        />
        <div className="flex items-center justify-between">
          <span className={`text-sm ${commentText.length > 2000 ? 'text-error-600' : 'text-gray-500'}`}>
            {commentText.length} / 2000 characters
            {commentText.trim().length > 0 && (
              <span className="ml-2 text-xs text-gray-400">
                (Cmd/Ctrl + Enter to post)
              </span>
            )}
          </span>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={
              commentText.trim().length === 0 ||
              commentText.length > 2000 ||
              createMutation.isPending
            }
            className="flex items-center gap-2"
          >
            {createMutation.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Posting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Post Comment
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Comments list */}
      {comments.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h4 className="text-lg font-medium text-gray-900 mb-1">No comments yet</h4>
          <p className="text-gray-600">
            Be the first to share your experience with this venue
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => {
            const isEditing = editingId === comment.id;
            const isDeleting = deleteConfirmId === comment.id;

            return (
              <Card key={comment.id} className="transition-shadow hover:shadow-md">
                <CardHeader className="flex-row items-start justify-between space-y-0 pb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <Avatar
                      name={comment.user_name || comment.user_email || 'User'}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">
                          {comment.user_name || comment.user_email}
                        </span>
                        {user?.id === comment.user_id && (
                          <span className="text-xs text-gray-500">(you)</span>
                        )}
                        {comment.is_edited && (
                          <span className="text-xs text-gray-500 italic">(edited)</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>

                  {/* Action buttons */}
                  {!isEditing && !isDeleting && (
                    <div className="flex items-center gap-1">
                      {canEditComment(comment) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(comment)}
                          aria-label="Edit comment"
                          className="p-2"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                      {canDeleteComment(comment) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirmId(comment.id)}
                          aria-label="Delete comment"
                          className="p-2 text-error-600 hover:text-error-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </CardHeader>

                <CardContent>
                  {isEditing ? (
                    /* Edit mode */
                    <div className="space-y-3">
                      <Textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={4}
                        maxLength={2000}
                        className="resize-y"
                        disabled={updateMutation.isPending}
                      />
                      <div className="flex items-center justify-between">
                        <span className={`text-sm ${editText.length > 2000 ? 'text-error-600' : 'text-gray-500'}`}>
                          {editText.length} / 2000 characters
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelEdit}
                            disabled={updateMutation.isPending}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleSaveEdit(comment.id)}
                            disabled={
                              editText.trim().length === 0 ||
                              editText.length > 2000 ||
                              updateMutation.isPending
                            }
                            className="flex items-center gap-2"
                          >
                            {updateMutation.isPending ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Saving...
                              </>
                            ) : (
                              'Save'
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : isDeleting ? (
                    /* Delete confirmation */
                    <div className="space-y-3">
                      <Alert variant="warning">
                        Are you sure you want to delete this comment? This action cannot be undone.
                      </Alert>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirmId(null)}
                          disabled={deleteMutation.isPending}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteConfirm(comment.id)}
                          disabled={deleteMutation.isPending}
                          className="flex items-center gap-2"
                        >
                          {deleteMutation.isPending ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            'Delete Comment'
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Display mode */
                    <p className="text-gray-800 whitespace-pre-wrap">{comment.comment_text}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

